import React, { useEffect, useState } from "react";
import { useToast } from "../../components/Toast";
import { HABI_API_BASE_URL, getAuthHeaders } from "../../utils/habiApi";
import ScreenHeader from "../../components/ScreenHeader";
import {
  colors,
  typography,
  cardBase,
  pageContainer,
  btnPrimary,
  btnSecondary,
  btnGhost,
} from "../../utils/designTokens";

/**
 * F4 — Knowledge Base interna de Habitia.
 * SuperAdmin puede agregar/buscar/borrar entries (resoluciones notables,
 * decisiones de comisiones, notas internas).
 * Staff (typeuser>=1) puede buscar.
 */
export default function KBHabitia({ setSelected }) {
  const { showToast } = useToast();

  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [recent, setRecent] = useState([]);

  // Add form fields
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [newTags, setNewTags] = useState("");
  const [newCategory, setNewCategory] = useState("general");
  const [newBuildingId, setNewBuildingId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadRecent();
  }, []);

  const loadRecent = async () => {
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/kb/entries?limit=15`, { headers });
      if (!resp.ok) return;
      const data = await resp.json();
      setRecent(data.items || []);
    } catch (e) {
      console.warn("[F4] load recent:", e);
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    try {
      setSearching(true);
      setResults([]);
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `${HABI_API_BASE_URL}/api/kb/search?q=${encodeURIComponent(query)}&k=10`,
        { headers }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "Error en la búsqueda.", "error");
        return;
      }
      const data = await resp.json();
      setResults(data.results || []);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSearching(false);
    }
  };

  const handleAdd = async () => {
    if (!newTitle.trim() || newContent.trim().length < 20) {
      showToast("Título y contenido (>20 chars) son requeridos.", "warning");
      return;
    }
    try {
      setSaving(true);
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/kb/entries`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          title: newTitle.trim(),
          content: newContent.trim(),
          tags: newTags.split(",").map(t => t.trim()).filter(Boolean),
          building_id: newBuildingId.trim() || null,
          category: newCategory,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "Error al agregar entrada.", "error");
        return;
      }
      showToast("Entrada agregada a la KB.", "success");
      setNewTitle("");
      setNewContent("");
      setNewTags("");
      setNewBuildingId("");
      setShowAddForm(false);
      loadRecent();
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("¿Borrar esta entrada de la KB? Esta acción es irreversible.")) return;
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/kb/entries/${entryId}`, {
        method: "DELETE",
        headers,
      });
      if (!resp.ok) {
        showToast("No se pudo borrar.", "error");
        return;
      }
      showToast("Entrada borrada.", "success");
      loadRecent();
      setResults((prev) => prev.filter((r) => r.metadata?.id !== entryId));
    } catch (e) {
      showToast(e.message, "error");
    }
  };

  return (
    <div style={pageContainer}>
      <ScreenHeader
        title="Knowledge Base interna"
        subtitle="Resoluciones, decisiones de comisiones, notas internas, casos legales"
      />

      {/* Caja de búsqueda */}
      <div style={{ ...cardBase, padding: 16, marginTop: 16 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Buscar en la KB: ej. mascotas, ruidos, asamblea, plan de pagos…"
            style={{
              flex: 1,
              padding: "10px 14px",
              fontSize: 14,
              border: `1px solid ${colors.gray200}`,
              borderRadius: 8,
              fontFamily: typography.fontFamily,
            }}
          />
          <button
            type="button"
            onClick={handleSearch}
            disabled={searching || !query.trim()}
            style={{
              ...btnPrimary,
              opacity: searching || !query.trim() ? 0.6 : 1,
              cursor: searching || !query.trim() ? "not-allowed" : "pointer",
            }}
          >
            {searching ? "Buscando…" : "Buscar"}
          </button>
          <button
            type="button"
            onClick={() => setShowAddForm((v) => !v)}
            style={{ ...btnSecondary, padding: "10px 14px" }}
          >
            {showAddForm ? "Cancelar" : "+ Agregar"}
          </button>
        </div>
      </div>

      {/* Form agregar */}
      {showAddForm && (
        <div style={{ ...cardBase, padding: 16, marginTop: 12 }}>
          <h3 style={{ margin: 0, fontSize: typography.sizes.md, fontWeight: 400, marginBottom: 12 }}>
            Nueva entrada en la KB
          </h3>
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Título corto (ej. Resolución mascotas grandes en Edificio X)"
            style={inputStyle}
          />
          <textarea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="Contenido detallado (mínimo 20 chars). Caso, resolución, decisión, notas..."
            rows={6}
            style={{ ...inputStyle, marginTop: 8, fontFamily: typography.fontFamily }}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
            <input
              type="text"
              value={newTags}
              onChange={(e) => setNewTags(e.target.value)}
              placeholder="Tags (coma): mascotas, perro, asamblea"
              style={inputStyle}
            />
            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              style={{ ...inputStyle, background: "#fff" }}
            >
              <option value="general">General</option>
              <option value="resolucion">Resolución</option>
              <option value="decision_comision">Decisión comisión</option>
              <option value="nota_interna">Nota interna</option>
              <option value="caso_legal">Caso legal</option>
            </select>
            <input
              type="text"
              value={newBuildingId}
              onChange={(e) => setNewBuildingId(e.target.value)}
              placeholder="building_id (opcional)"
              style={inputStyle}
            />
          </div>
          <button
            type="button"
            onClick={handleAdd}
            disabled={saving}
            style={{ ...btnPrimary, marginTop: 12, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? "Guardando…" : "Guardar entrada"}
          </button>
        </div>
      )}

      {/* Resultados de búsqueda */}
      {results.length > 0 && (
        <div style={{ ...cardBase, padding: 16, marginTop: 16 }}>
          <h3 style={{ margin: 0, fontSize: typography.sizes.md, fontWeight: 400, marginBottom: 12 }}>
            Resultados ({results.length})
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {results.map((r, i) => (
              <KBResultCard key={i} item={r} onDelete={handleDelete} />
            ))}
          </div>
        </div>
      )}

      {/* Recientes */}
      {results.length === 0 && recent.length > 0 && (
        <div style={{ ...cardBase, padding: 16, marginTop: 16 }}>
          <h3 style={{ margin: 0, fontSize: typography.sizes.md, fontWeight: 400, marginBottom: 12 }}>
            Entradas recientes ({recent.length})
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {recent.map((r, i) => (
              <KBResultCard key={i} item={r} onDelete={handleDelete} />
            ))}
          </div>
        </div>
      )}

      {results.length === 0 && recent.length === 0 && (
        <div style={{ ...cardBase, padding: 24, marginTop: 16, textAlign: "center" }}>
          <p style={{ color: colors.gray600, fontSize: 13 }}>
            La KB está vacía. Empezá agregando casos notables que valga la pena que otros staff puedan consultar.
          </p>
        </div>
      )}
    </div>
  );
}

function KBResultCard({ item, onDelete }) {
  const meta = item.metadata || {};
  const tags = (meta.tags || "").split(",").map(t => t.trim()).filter(Boolean);
  const previewContent = (item.content || "").slice(0, 400);

  return (
    <div style={{
      padding: 12,
      background: colors.gray50,
      borderRadius: 8,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <strong style={{ fontWeight: 400, fontSize: 14, color: colors.gray900 }}>
          {meta.title || "(sin título)"}
        </strong>
        {item.score !== undefined && (
          <span style={{ fontSize: 11, color: colors.gray600 }}>
            match {Math.round(item.score * 100)}%
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: colors.gray700, whiteSpace: "pre-wrap", marginBottom: 8 }}>
        {previewContent}{(item.content || "").length > 400 && "…"}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {meta.category && (
          <span style={{
            padding: "2px 10px",
            fontSize: 11,
            borderRadius: 9999,
            background: colors.primaryLighter,
            color: colors.primary,
          }}>
            {meta.category}
          </span>
        )}
        {tags.map((t, i) => (
          <span key={i} style={{
            padding: "2px 10px",
            fontSize: 11,
            borderRadius: 9999,
            background: colors.gray100,
            color: colors.gray700,
          }}>
            {t}
          </span>
        ))}
        {meta.building_id && (
          <span style={{ fontSize: 11, color: colors.gray600 }}>
            · edificio {meta.building_id}
          </span>
        )}
        {meta.author_email && (
          <span style={{ fontSize: 11, color: colors.gray600 }}>
            · por {meta.author_email}
          </span>
        )}
        {meta.created_at && (
          <span style={{ fontSize: 11, color: colors.gray600 }}>
            · {meta.created_at.slice(0, 10)}
          </span>
        )}
        <button
          type="button"
          onClick={() => onDelete && onDelete(meta.id || "")}
          style={{
            marginLeft: "auto",
            padding: "2px 10px",
            fontSize: 11,
            color: colors.error,
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
        >
          Borrar
        </button>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 13,
  border: `1px solid ${colors.gray200}`,
  borderRadius: 8,
  fontFamily: "inherit",
  boxSizing: "border-box",
};
